import React, { useState, useEffect } from 'react';
import { ListChecks, Loader2, Bot } from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const RISK_CLASS = {
	Critical: 'risk-badge risk-high',
	High: 'risk-badge risk-high',
	Medium: 'risk-badge risk-medium',
	Low: 'risk-badge risk-low',
};

const POAMTab = ({ scanId }) => {
    const [poamData, setPoamData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [cached, setCached] = useState(false);

    useEffect(() => {
        const fetchCachedPOAM = async () => {
            setLoading(true);
            setError(null);
            try {
                const cachedResponse = await fetch(ApiNames.AI_HUB_FETCH_CACHED_AI_DATA(scanId, "POAM"), { method: 'POST', credentials: 'include' });
                if (cachedResponse.status == 200) {
                    const json = await cachedResponse.json();
                    const poam = JSON.parse(json.response);
                    console.log(json);
                    setPoamData({
                        poam: poam,
                        tokensUsed: json.tokens_used,
                        costUsd: json.cost_usd,
                        lastUpdated: json.lastUpdated,
                    });
                    console.log(poamData.poam);
                    setCached(true);
                } else {
                    console.log("Cached POAM not found");
                    setCached(false);
                }
            } catch (err) {
                setError('Failed to load POAM');
            } finally {
                setLoading(false);
            }
        };
        fetchCachedPOAM();
    }, [scanId]);

    const handleGenerate = async () => {
        setLoading(true);
        setError(null);
        setCached(false);
        try {
            const response = await fetch(ApiNames.AI_HUB_GENERATE_POAM(scanId), { method: 'POST', credentials: 'include' });
            if (!response.ok) throw new Error('Request failed');
            const json = await response.json();
            const poam = JSON.parse(json.response);
            setPoamData({
                poam: poam,
                tokensUsed: json.tokens_used,
                costUsd: json.cost_usd,
            });
        } catch (err) {
            setError('Unable to load POA&M data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const generatePdf = (columnHeaders, rows) => {
        const doc = new jsPDF({ orientation: 'landscape' });
        autoTable(doc, {
            head: [columnHeaders],
            body: rows,
            columnStyles: {
                0: { cellWidth: 20 }, 
            },
        });
        doc.save('POA&M.pdf');
    };

    const fetchExcel = async () => {
        try {
            const response = await fetch(ApiNames.AI_HUB_GENERATE_POAM_EXCEL(scanId));
            
            if (!response.status == 200) {
                throw new Error('Network response was not ok');
            }

            // Convert the response to a Blob
            const blob = await response.blob();

            // Create a URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary anchor tag for the download
            const a = document.createElement('a');
            a.href = url;
            a.download = 'POA&M.xlsx'; // The filename for the user's saved file

            // Append to the DOM, click it, and remove it
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url); // Clean up the object URL
            document.body.removeChild(a);

        } catch (error) {
            console.error('Download failed:', error);
            alert('Failed to download the file.');
        }
    };

    return (
        <div className="flex flex-col items-center gap-8">
            <div className="w-full max-w-6xl">
                <div className="card-header flex items-center justify-between mb-2">
                    <div className="card-title flex items-center gap-2">
                        <ListChecks className="w-6 h-6 text-teal-400" />
                        <h3 className="font-bold text-lg">AI Auto-POA&M Generator</h3>
                    </div>
                    <span className="card-badge flex items-center gap-1 bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs font-semibold">
                        <Bot className="w-4 h-4 mr-1" />Auto-Generated
                    </span>
                </div>
                <p className="text-gray-400 mb-6 text-sm mt-2">
                    Automatically generated Plan of Action & Milestones table based on scan findings. Track weaknesses, risks, milestones, and owners for compliance remediation.
                </p>
                    <div className="w-full overflow-x-auto">
                        {!cached && !poamData.poam && !loading && !error && (
                            <div className="flex flex-col items-center py-12">
                                <button
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150"
                                    onClick={handleGenerate}
                                    disabled={loading || !scanId}
                                >
                                    Generate POA&M
                                </button>
                            </div>
                        )}
                        {loading && (
                            <div className="ai-response flex flex-col items-center justify-center py-12 animate-fadeIn">
                                <Loader2 className="animate-spin text-teal-400 w-10 h-10 mb-4" />
                                <span className="text-gray-400">Generating POA&M table...</span>
                            </div>
                        )}
                        {error && !loading && (
                            <div className="ai-response text-center text-red-400 py-12">{error}</div>
                        )}
                        {poamData?.poam?.length > 0 && !loading && !error && (
                            <>
                            <table className="poam-table min-w-full bg-base-100 rounded-xl shadow-xl animate-fadeIn mb-4 border-spacing-8 border-separate">
                                <thead>
                                    <tr>
                                        <th>POA&M ID</th>
                                        <th>Weakness</th>
                                        <th>Risk</th>
                                        <th>Milestones</th>
                                        <th>Owner</th>
                                        <th>Due Date</th>
                                        <th>Resources</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {poamData?.poam.map((item, idx) => (
                                        <tr key={item['POA&M ID'] || idx}>
                                            <td>{item['POA&M ID']}</td>
                                            <td>
                                                <div>
                                                    <span className="font-semibold text-base text-black">{item['Weakness Name']}</span><br />
                                                    <span className="text-sm text-gray-500">{item['Weakness Description']}</span><br />
                                                    <span className="text-xs italic text-gray-400">{item['Controls']}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={RISK_CLASS[item['Risk']] || 'risk-badge'}>{item['Risk']}</span>
                                            </td>
                                            <td>{item['Planned Milestones']}</td>
                                            <td>{item['Owner']}</td>
                                            <td>{item['Due Date'] === 'BLANK' ? <span className="text-gray-400">—</span> : item['Due Date']}</td>
                                            <td>{item['Resources Required']}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="flex flex-col items-center py-6">
                                <p className="mb-2 text-gray-400">Tokens used: {poamData.tokensUsed}. Cost: ${poamData.costUsd}.</p>
                                {cached && (
                                    <p className="mb-2 text-gray-400">Viewing cached POA&M. Last updated: {poamData.lastUpdated}.</p>
                                )}
                                <div className="flex flex-row gap-x-2">
                                    <button
                                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150"
                                        onClick={() => handleGenerate}
                                        disabled={loading || !scanId}
                                    >
                                        Regenerate POA&M
                                    </button>
                                    <button
                                        className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150"
                                        onClick={() => fetchExcel()}
                                        disabled={loading || !scanId}
                                    >
                                        Export to Excel
                                    </button>
                                    <button
                                        className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150"
                                        onClick={() => generatePdf(Object.keys(poamData.poam[0]), poamData.poam.map((item) => Object.values(item)))}
                                        disabled={loading || !scanId}
                                    >
                                        Export to PDF
                                    </button>
                                </div>
                            </div>
                            </>
                        )}
                    </div>
            </div>
        </div>
    );
};

export default POAMTab;
